import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import Seo from "@/components/feature/Seo";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import useHashScroll from "@/hooks/useHashScroll";
import { getArticlesByCategory } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function TopicPage() {
  const { slug } = useParams<{ slug: string }>();
  useHashScroll();

  const category = categories.find((item) => item.slug === slug);
  const list = slug ? getArticlesByCategory(slug) : [];
  const others = categories.filter((item) => item.slug !== slug);

  if (!category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <h1 className="font-heading text-2xl font-black text-foreground-950">
            这个主题暂时不存在
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            也许是链接有了变化。你可以先看看全部主题档案。
          </p>
          <Link
            to="/topics"
            className="mt-7 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            返回主题档案
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Seo
        title={`${category.name} - 事件解读专题 | 15黑料网`}
        description={`${category.description} 15黑料网${category.name}专题共收录 ${list.length} 篇事件解读。`}
        keywords={`${category.name},${category.keywords.join(",")},15黑料网`}
        canonicalPath={`/topic/${category.slug}`}
        ogImage={category.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 事件解读专题`,
            url: `https://sznita.cn/topic/${category.slug}`,
            description: category.description,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://sznita.cn/" },
              { "@type": "ListItem", position: 2, name: "主题档案", item: "https://sznita.cn/topics" },
              {
                "@type": "ListItem",
                position: 3,
                name: category.name,
                item: `https://sznita.cn/topic/${category.slug}`,
              },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="relative w-full overflow-hidden border-b border-background-200">
          <div className="absolute inset-0">
            <img
              src={category.cover}
              alt={`${category.name}专题页背景图`}
              title={`${category.name} - 15黑料网主题档案`}
              className="h-full w-full object-top object-cover"
            />
            <div className="absolute inset-0 bg-background-950/75" />
          </div>
          <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-12 pt-8 md:px-8 md:pb-16 md:pt-10">
            <div className="[&_a]:text-background-200 [&_span]:text-background-100">
              <Breadcrumbs
                items={[{ label: "主题档案", path: "/topics" }, { label: category.name }]}
              />
            </div>
            <p className="label-caps mt-6 text-[11px] text-accent-300">
              {category.english} · {list.length} 篇解读
            </p>
            <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-4xl">
              {category.name}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
              {category.description}
            </p>
          </div>
        </section>

        <section aria-label="该主题下的解读" className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-14">
            {list.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((article, index) => (
                  <Reveal key={article.id} delay={(index % 3) * 70}>
                    <ArticleCard article={article} variant="feature" />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
                <p className="font-heading text-lg font-bold text-foreground-950">
                  这个方向下的解读正在补充中
                </p>
                <p className="mt-2 text-sm text-foreground-600">
                  可以先浏览其他主题，或直接进入解读库搜索关键词。
                </p>
              </div>
            )}

            <div className="mt-14 border-t border-background-200 pt-8">
              <h2 className="font-heading text-lg font-black text-foreground-950">
                其他主题档案
              </h2>
              <div className="mt-5 flex flex-wrap gap-3">
                {others.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/topic/${item.slug}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                  >
                    {item.name}
                    <i className="ri-arrow-right-up-line text-xs" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}